import { useEffect, useMemo, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import {
	type UseResponseStyleSettingsParams,
	useResponseStyleSettings,
} from '@atlassian/conversation-assistant-store/controllers/response-style-settings';

import { GREETING_ILLUSTRATIONS } from './greetingIllustrations';
import { greetingRotation } from './greetingRotation';
import { GREETING_SETS, type GreetingMode } from './greetingSets';

/** A resolved greeting ready to render: a formatted line plus optional illustration overrides. */
export type PersonalityGreeting = {
	title: string;
	src?: string;
	srcDark?: string;
};

export type UsePersonalityGreetingResult = {
	/** The resolved greeting, or `null` when personality greetings don't apply (empty set). */
	greeting: PersonalityGreeting | null;
	/**
	 * True while the active preset is not yet known (settings query still resolving
	 * on a cold open). Callers can render their default greeting until the preset resolves.
	 */
	isPending: boolean;
};

export type UsePersonalityGreetingParams = {
	/** Active empty-state mode, mapped to the greeting set. */
	mode: GreetingMode;
	/** User's first name, templated into greetings that use `{name}`. */
	firstName: string;
	assistanceServiceParams: UseResponseStyleSettingsParams['assistanceServiceParams'];
};

/**
 * Resolves the personality-flavored empty-state greeting for the active
 * (mode, preset) pair. Reads the preset from `useResponseStyleSettings` and
 * shuffles within the set per session (see `greetingRotation`). Reports
 * `isPending` until the preset is known so callers can retain their fallback.
 */
export const usePersonalityGreeting = ({
	mode,
	firstName,
	assistanceServiceParams,
}: UsePersonalityGreetingParams): UsePersonalityGreetingResult => {
	const { formatMessage } = useIntl();
	const { responseStyle, isLoading } = useResponseStyleSettings({ assistanceServiceParams });
	const tone = responseStyle.responseTone;

	const set = GREETING_SETS[mode][tone];
	const key = `${mode}:${tone}`;
	const canSelect = !isLoading && set.length > 0;

	const selectedKeysRef = useRef<Set<string>>(new Set());
	const [selected, setSelected] = useState<Record<string, number>>({});

	useEffect(() => {
		if (!canSelect || selectedKeysRef.current.has(key)) {
			return;
		}
		selectedKeysRef.current.add(key);
		const index = greetingRotation.next(key, set.length);
		setSelected((previous) => ({ ...previous, [key]: index }));
	}, [canSelect, key, set.length]);

	const result = useMemo<UsePersonalityGreetingResult>(() => {
		if (set.length === 0) {
			return { greeting: null, isPending: false };
		}
		if (isLoading) {
			return { greeting: null, isPending: true };
		}
		const index = selected[key];
		if (index === undefined || index < 0) {
			return { greeting: null, isPending: true };
		}
		const illustration = GREETING_ILLUSTRATIONS[tone];
		return {
			greeting: {
				title: formatMessage(set[index], { name: firstName }),
				src: illustration?.src,
				srcDark: illustration?.srcDark,
			},
			isPending: false,
		};
	}, [isLoading, selected, key, set, tone, formatMessage, firstName]);

	return result;
};
